import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from 'antd';
import {
  exportSalesSummaryReport,
  fetchCustomerGroups,
  fetchSalesSummary,
} from '../../services/ordersService';
import Loader, { ButtonLoader } from '../../../../components/Loader';
import {
  showSuccessToast,
  showWarningToast,
} from '../../../../utils/toastHelper';
import {
  formatDecimalTwoDigits,
  formatSpecification,
  trimDecimalZero,
} from '../../../../utils/commonUtils';
import { printTable } from '../../../../utils/printUtils';
import FileNames from '../../../../constants/fileNames';
const { RangePicker } = DatePicker;

const SalesSummary = () => {
  const { t } = useTranslation('Order');

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [deliveryDateFilter, setDeliveryDateFilter] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());

  const [filters, setFilters] = useState({
    customerGroupFilter: [],
  });
  const [isPrinting, setIsPrinting] = useState(false);
  const isInitialMount = useRef(true);

  const fetchData = async (ifSearch = 0) => {
    setLoading(true);

    try {
      const query = {
        search: searchQuery,
        customerGroups: filters.customerGroupFilter,
        deliveryDateFilter,
      };

      if (ifSearch) {
        const reportResponse = await fetchSalesSummary(query);
        if (reportResponse.report_data) {
          setReportData(reportResponse.report_data);
        }
      } else {
        const [reportResponse, customerGroupResponse] = await Promise.all([
          fetchSalesSummary(query),
          fetchCustomerGroups({ per_page: 9999, category: 'FBZ' }),
        ]);
        if (reportResponse.report_data) {
          setReportData(reportResponse.report_data);
        }
        if (customerGroupResponse.customer_groups) {
          setCustomerGroups(customerGroupResponse.customer_groups);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  const {
    totalQuantity,
    totalAmount,
    selectedTotalAmount,
    selectedTotalQuantity,
  } = useMemo(() => {
    const result = reportData.reduce(
      (acc, item) => {
        acc.totalQuantity += parseFloat(item.total_quantity);
        acc.totalAmount += parseFloat(item.total_amount);

        if (selectedRows.has(`${item.delivery_address}|||${item.product_id}`)) {
          acc.selectedTotalQuantity += parseFloat(item.total_quantity);
          acc.selectedTotalAmount += parseFloat(item.total_amount);
        }

        return acc;
      },
      {
        totalQuantity: 0,
        totalAmount: 0,
        selectedTotalQuantity: 0,
        selectedTotalAmount: 0,
      }
    );

    return {
      totalQuantity: result.totalQuantity.toFixed(2),
      totalAmount: result.totalAmount.toFixed(2),
      selectedTotalQuantity: result.selectedTotalQuantity.toFixed(2),
      selectedTotalAmount: result.selectedTotalAmount.toFixed(2),
    };
  }, [reportData, selectedRows]);

  // Initial data load
  useEffect(() => {
    fetchData(0);
  }, []);

  useEffect(() => {
    // Skip the first render since we already loaded data in the first useEffect
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      fetchData(1);
      setSelectedRows(new Set());
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, filters, deliveryDateFilter]);

  const handleSelectAllChange = (e) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      // Create unique identifiers for all rows using a separator that's unlikely to appear in the data
      const allItemIds = reportData.map(
        (row) => `${row.delivery_address}|||${row.product_id}`
      );
      setSelectedRows(new Set(allItemIds));
    } else {
      setSelectedRows(new Set());
    }
  };
  const handleDateFilterChange = (date, dateString) => {
    setDeliveryDateFilter(dateString);
  };
  const handleFilterChange = (filterType, value) => {
    console.log('on filter change.....', filterType, value);
    setFilters((prev) => {
      const newFilterState = { ...prev };

      // Handle "ALL" selection separately
      if (value === 'ALL') {
        // Toggle all items for the current filter type (categories, origins, or process locations)
        newFilterState[filterType] =
          newFilterState[filterType].length ===
          filterTypeData[filterType].length
            ? [] // Deselect all
            : filterTypeData[filterType].map((item) => item.id); // Select all
      } else {
        // Handle individual item selection/deselection
        newFilterState[filterType] = newFilterState[filterType].includes(value)
          ? newFilterState[filterType].filter((item) => item !== value) // Deselect the item
          : [...newFilterState[filterType], value]; // Select the item
      }

      return newFilterState;
    });
  };

  // Define the filter data for each type dynamically
  const filterTypeData = {
    customerGroupFilter: customerGroups,
  };
  const handleExportReport = async () => {
    const data = {
      search: searchQuery,
      customerGroups: filters.customerGroupFilter,
      deliveryDateFilter,
    };

    if (selectedRows.size !== 0) {
      // Extract delivery_address and product_id pairs from selected rows

      // Send the selected items in a format that's easy to query in the backend
      if (isAllSelected) {
        data.selectAll = isAllSelected;
      } else {
        const selectedItems = Array.from(selectedRows).map((identifier) => {
          const [delivery_address, product_id] = identifier.split('|||');
          return { delivery_address, product_id };
        });
        data.selectedRows = selectedItems;
      }
    }

    const response = await exportSalesSummaryReport(
      data,
      FileNames.CUSTOMER_SALES_SUMMARY_REPORT_EXPORT
    );
    // if (response.success) {
    //   showSuccessToast(response.message);
    // }
  };
  const handleRowSelect = (e, row) => {
    const isChecked = e.target.checked;
    const rowIdentifier = `${row.delivery_address}|||${row.product_id}`;

    setSelectedRows((prevSelected) => {
      const updatedSelection = new Set(prevSelected);
      if (isChecked) {
        updatedSelection.add(rowIdentifier);
      } else {
        updatedSelection.delete(rowIdentifier);
      }
      return updatedSelection;
    });
  };

  const isAllSelected =
    reportData.length > 0 &&
    reportData.every((row) =>
      selectedRows.has(`${row.delivery_address}|||${row.product_id}`)
    );

  const handlePrint = () => {
    if (selectedRows.size === 0) {
      showWarningToast(t('NOROWSELECTED'));
      return;
    }

    const headers = [
      t('CUSTOMER'),
      t('CATEGORY'),
      t('PROCESSCATEGORY'),
      t('PRODUCTCODE'),
      t('PRODUCTNAME'),
      t('ORIGIN'),
      t('SPECIFICATION'),
      t('TOTALQUANTITY'),
      t('TOTALAMOUNT'),
    ];
    const selectedReportData = reportData.filter((row) => {
      const rowIdentifier = `${row.delivery_address}|||${row.product_id}`;
      return selectedRows.has(rowIdentifier);
    });
    const content = selectedReportData.map((row) => [
      (row.customer_group || '') + '<br>' + (row.customer_group_code || ''),

      row.product_category || '',
      row.process_category || '',
      row.product_code || '',
      row.product_name || '',
      row.origin || '',
      formatSpecification(row),
      trimDecimalZero(row.total_quantity),
      '$' + formatDecimalTwoDigits(row.total_amount),
    ]);

    printTable(
      headers,
      content,
      'Customer Sales Summary Report',
      setIsPrinting,
      false,
      {
        7: trimDecimalZero(selectedTotalQuantity),
        8: '$' + formatDecimalTwoDigits(selectedTotalAmount),
      }
    );
  };
  return (
    <main className="product product-page report-page  sales-summary">
      <section className="main-section">
        <section className="data on-responsive-block">
          <div className="row">
            <div className="col-sm-12 col-xl-4 col-md-4">
              <div className="left">
                <h1>{t('CUSTOMERSALESSUMMARYREPORT')}</h1>
                <div className="input-div">
                  <div className="input-card">
                    <img src="images/search-icon.svg" alt="Search" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('SEARCH')}
                    />
                  </div>
                  <div className="filter-section date">
                    <label>{t('DELIVERYDATE')}</label>
                    <div className={'date-picker range-picker'}>
                      <RangePicker
                        onChange={handleDateFilterChange}
                        separator={'-'}
                        placeholder={[t('FROM'), t('TO')]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-xl-4 col-sm-12">
              <div className="right">
                <div className="filter-container">
                  <div className="filter-section group-name">
                    <label>{t('CUSTOMERGROUPNAME')}</label>
                    <div className="checkbox-group pro-filter">
                      <label className="custom-checkbox">
                        {t('ALL')}
                        <input
                          type="checkbox"
                          checked={
                            filters.customerGroupFilter.length ===
                            customerGroups.length
                          }
                          onChange={() =>
                            handleFilterChange('customerGroupFilter', 'ALL')
                          }
                        />
                        <span className="checkmark"></span>
                      </label>

                      {customerGroups.map((customerGroup) => (
                        <label
                          key={customerGroup.id}
                          className="custom-checkbox"
                        >
                          {customerGroup.group}
                          <input
                            type="checkbox"
                            checked={filters.customerGroupFilter.includes(
                              customerGroup.id
                            )}
                            onChange={() =>
                              handleFilterChange(
                                'customerGroupFilter',
                                customerGroup.id
                              )
                            }
                          />
                          <span className="checkmark"></span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-xl-4 col-sm-12">
              <div className="right right-change-cs">
                <div className="button-group">
                  <button onClick={handleExportReport}>
                    <img src="images/export.svg" alt="" />
                    {t('EXPORT')}
                  </button>
                  {/* <button type="button">
                      <img src="images/print.svg" alt="Print" />
                      Print
                    </button> */}
                  <button
                    disabled={
                      reportData.length === 0 || selectedRows.size === 0
                    }
                    className={`${reportData.length === 0 || selectedRows.size === 0 ? 'disable-btn' : ''}`}
                    onClick={handlePrint}
                  >
                    {isPrinting ? (
                      <ButtonLoader />
                    ) : (
                      <>
                        <img src="images/print.svg" alt="" />
                        {t('PRINT')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            {/* <div className="col-sm-6">
              <div className="left">
                <h1>{t('CUSTOMERSALESSUMMARYREPORT')}</h1>
                <div className="input-card">
                  <img src="images/search-icon.svg" alt="Search" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('SEARCH')}
                  />
                </div>
                <div className="filter-section date">
                  <label>{t('DELIVERYDATE')}</label>
                  <div className={'date-picker range-picker'}>
                    <RangePicker
                      onChange={handleDateFilterChange}
                      separator={'-'}
                      placeholder={[t('FROM'), t('TO')]}
                    />
                  </div>
                </div>
              </div>
            </div> */}
            {/* <div className="col-sm-6">
              <div className="right">
                <div className="filter-container"> */}
            {/* <div className="filter-section date">
                    <label>{t('DELIVERYDATE')}</label>
                    <div className={'date-picker range-picker'}>
                      <RangePicker
                        onChange={handleDateFilterChange}
                        separator={'-'}
                        placeholder={[t('FROM'), t('TO')]}
                      />
                    </div>
                  </div> */}
            {/* 
                  <div className="filter-section group-name">
                    <label>{t('CUSTOMERGROUPNAME')}</label>
                    <div className="checkbox-group pro-filter">
                      <label className="custom-checkbox">
                        {t('ALL')}
                        <input
                          type="checkbox"
                          checked={
                            filters.customerGroupFilter.length ===
                            customerGroups.length
                          }
                          onChange={() =>
                            handleFilterChange('customerGroupFilter', 'ALL')
                          }
                        />
                        <span className="checkmark"></span>
                      </label>

                      {customerGroups.map((customerGroup) => (
                        <label
                          key={customerGroup.id}
                          className="custom-checkbox"
                        >
                          {customerGroup.group}
                          <input
                            type="checkbox"
                            checked={filters.customerGroupFilter.includes(
                              customerGroup.id
                            )}
                            onChange={() =>
                              handleFilterChange(
                                'customerGroupFilter',
                                customerGroup.id
                              )
                            }
                          />
                          <span className="checkmark"></span>
                        </label>
                      ))}
                    </div>
                  </div> */}
            {/* 
                  <div className="button-group">
                    <button onClick={handleExportReport}>
                      <img src="images/export.svg" alt="" />
                      {t('EXPORT')}
                    </button> */}
            {/* <button type="button">
                      <img src="images/print.svg" alt="Print" />
                      Print
                    </button> */}
            {/* <button
                      disabled={
                        reportData.length === 0 || selectedRows.size === 0
                      }
                      className={`${reportData.length === 0 || selectedRows.size === 0 ? 'disable-btn' : ''}`}
                      onClick={handlePrint}
                    >
                      {isPrinting ? (
                        <ButtonLoader />
                      ) : (
                        <>
                          <img src="images/print.svg" alt="" />
                          {t('PRINT')}
                        </>
                      )}
                    </button>
                  </div> */}
            {/* </div>
              </div>
            </div> */}
          </div>
        </section>

        {/* Table with additional columns */}
        <div className="sales-reports-table">
          <div
            className="table-outer"
            style={{
              borderBottom:
                loading || reportData.length === 0 ? '1px solid black' : '',
              borderRadius: loading || reportData.length === 0 ? '10px' : '',
            }}
          >
            <table className="table">
              <thead>
                <tr className="t-heading">
                  <th className="t-checkbox">
                    <label className="container">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={handleSelectAllChange}
                      />
                      <span className="checkmark"></span>
                    </label>
                  </th>
                  <th className="customer">
                    <h5>{t('CUSTOMER')}</h5>
                  </th>
                  <th className="category">
                    <h5>{t('CATEGORY')}</h5>
                  </th>
                  <th className="process-category">
                    <h5>{t('PROCESSCATEGORY')}</h5>
                  </th>
                  <th className="code">
                    <h5>{t('PRODUCTCODE')}</h5>
                  </th>
                  <th className="name">
                    <h5>{t('PRODUCTNAME')}</h5>
                  </th>
                  <th className="origin">
                    <h5>{t('ORIGIN')}</h5>
                  </th>
                  <th className="specification">
                    <h5>{t('SPECIFICATION')}</h5>
                  </th>
                  <th className="total total-anlysis">
                    <h5>{t('TOTALQUANTITY')}</h5>
                  </th>
                  <th className="total">
                    <h5>{t('TOTALAMOUNT')}</h5>
                  </th>
                </tr>
              </thead>
              {loading ? (
                <tbody>
                  <tr style={{ height: '100px' }}>
                    <td colSpan="100%" className="p-0">
                      <Loader />
                    </td>
                  </tr>
                </tbody>
              ) : (
                <>
                  <tbody>
                    {reportData.length === 0 ? (
                      <tr>
                        <td colSpan="100%" className="text-center py-4">
                          {t('NORECORDAVAILABLE')}
                        </td>
                      </tr>
                    ) : (
                      <>
                        {reportData.map((row, index) => (
                          <tr key={index}>
                            <td className="t-checkbox">
                              <label className="container">
                                <input
                                  type="checkbox"
                                  checked={selectedRows.has(
                                    `${row.delivery_address}|||${row.product_id}`
                                  )}
                                  onChange={(e) => handleRowSelect(e, row)}
                                />
                                <span className="checkmark"></span>
                              </label>
                            </td>
                            <td className="customer">
                              {/* <h5>{row.customer_group}</h5> */}
                              <span className="d-block">
                                {row.customer_group || ''}
                              </span>

                              <span className="d-block">
                                {row.customer_group_code || ''}
                              </span>
                            </td>
                            <td className="category">
                              <h5>{row.product_category}</h5>
                            </td>
                            <td className="process-category">
                              <h5>{row.process_category}</h5>
                            </td>
                            <td className="code">
                              <h5>{row.product_code}</h5>
                            </td>
                            <td className="name">
                              <h5>{row.product_name}</h5>
                            </td>
                            <td className="origin">
                              <h5>{row.origin}</h5>
                            </td>
                            <td className="specification">
                              <h5>{formatSpecification(row)}</h5>
                            </td>
                            <td className="total ">
                              <h5>{trimDecimalZero(row.total_quantity)}</h5>
                            </td>
                            <td className="total">
                              <h5>
                                ${formatDecimalTwoDigits(row.total_amount)}
                              </h5>
                            </td>
                          </tr>
                        ))}
                        {/* <tr className="total-data">
                        <td colSpan="7"></td>
                        <td className="category">
                          <h5 className="fw-bold">{t('TOTAL')}</h5>
                        </td>
                        <td className="total">
                          <h5 className="fw-bold">{totalQuantity || ''}</h5>
                        </td>
                        <td className="total">
                          <h5 className="fw-bold">${totalAmount || '0.00'}</h5>
                        </td>
                      </tr> */}
                      </>
                    )}
                  </tbody>

                  {reportData.length !== 0 && (
                    <tfoot>
                      <tr className="total-data">
                        <th className="t-checkbox"></th>
                        <th className="customer">
                          {/* <h5>{row.customer_group}</h5> */}
                          <span className="d-block"></span>
                          <span className="d-block"></span>
                        </th>
                        <th className="category">
                          <h5></h5>
                        </th>
                        <th className="process-category">
                          <h5></h5>
                        </th>
                        <th className="code">
                          <h5></h5>
                        </th>
                        <th className="name">
                          <h5></h5>
                        </th>
                        <th className="origin">
                          <h5></h5>
                        </th>
                        <th className="specification">
                          <h5 className="fw-bold">{t('TOTAL')}</h5>
                        </th>
                        <th className="total-qty ">
                          <h5 className="fw-bold">
                            {trimDecimalZero(totalQuantity) || ''}
                          </h5>
                        </th>
                        <th className="total">
                          <h5 className="fw-bold">
                            ${formatDecimalTwoDigits(totalAmount) || '0.00'}
                          </h5>
                        </th>
                      </tr>
                    </tfoot>
                  )}
                </>
              )}
            </table>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SalesSummary;
